// src/app/shared/services/seo.service.ts

import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

export interface SeoData {
  title: string;
  description: string;
  url: string;
  image?: string;
  type?: 'website' | 'article';
  keywords?: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  private resolveImageUrl(image?: string): string {
    const defaultImage = 'https://segurosvirtual.pt/assets/logotipos/LogoLogin.webp';
    if (!image || typeof image !== 'string') {
      return defaultImage;
    }
    const trimmed = image.trim();
    if (!trimmed || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
      return defaultImage;
    }
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return trimmed.startsWith('/') ? `https://segurosvirtual.pt${trimmed}` : `https://segurosvirtual.pt/${trimmed}`;
  }

  setSEO(data: SeoData): void {
    const imageUrl = this.resolveImageUrl(data.image);
    const siteName = 'Seguros Virtual';
    const ogType = data.type || 'website';
    const cleanUrl = data.url.startsWith('/') ? data.url : `/${data.url}`;

    this.title.setTitle(data.title);
    this.meta.updateTag({ name: 'description', content: data.description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ name: 'author', content: siteName });

    if (data.keywords) {
      this.meta.updateTag({ name: 'keywords', content: data.keywords });
    }

    // Canonical dinâmico — cada página tem o seu URL canónico correcto
    this.updateCanonical(cleanUrl);

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: data.title });
    this.meta.updateTag({ property: 'og:description', content: data.description });
    this.meta.updateTag({ property: 'og:type', content: ogType });
    this.meta.updateTag({ property: 'og:url', content: `https://segurosvirtual.pt${cleanUrl}` });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ property: 'og:site_name', content: siteName });

    // Twitter
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: data.title });
    this.meta.updateTag({ name: 'twitter:description', content: data.description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    this.meta.updateTag({ name: 'twitter:site', content: '@SegurosVirtual' });
  }

  setTitleOnly(title: string): void {
    this.title.setTitle(title);
  }

  setNoIndex(): void {
    this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
  }

  setIndex(): void {
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
  }

  /**
   * Actualiza dinamicamente o <link rel="canonical"> e <link rel="alternate" hreflang="pt-PT"> no <head>.
   * Se não existir, cria-o. Se já existir, actualiza o href.
   * @param url Caminho relativo da página (ex: '/seguros/viagem')
   */
  updateCanonical(url: string): void {
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    const canonicalUrl = `https://segurosvirtual.pt${cleanUrl}`;
    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);

    const hreflangLink: HTMLLinkElement | null = this.document.querySelector('link[rel="alternate"][hreflang="pt-PT"]');
    if (hreflangLink) {
      hreflangLink.setAttribute('href', canonicalUrl);
    }
  }

  /**
   * Injeta dinamicamente um bloco JSON-LD no <head> do documento.
   * Usa um ID para evitar duplicações — se já existir, remove o anterior.
   * @param id Identificador único do bloco JSON-LD (ex: 'seo-services')
   * @param data Objeto com os dados estruturados Schema.org
   */
  setDynamicJsonLd(id: string, data: object): void {
    // Remover bloco existente com o mesmo ID
    this.removeDynamicJsonLd(id);

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `jsonld-${id}`;
    script.text = JSON.stringify(data);
    this.document.head.appendChild(script);
  }

  /**
   * Remove um bloco JSON-LD dinâmico pelo seu ID.
   * @param id Identificador único do bloco JSON-LD
   */
  removeDynamicJsonLd(id: string): void {
    const existing = this.document.getElementById(`jsonld-${id}`);
    if (existing) {
      existing.remove();
    }
  }
}
